import React from 'react'
import { Link } from 'gatsby'
import { ReactComponent as DiscordSVG } from './discord.svg'
import { ReactComponent as GithubSVG } from './github.svg'
import { ReactComponent as LinkedInSvg } from './linkedin.svg'
import { ReactComponent as TwitterSvg } from './twitter.svg'
import { ReactComponent as InvitationSVG } from './invitation.svg'

import { ReactComponent as FpjsSVG } from '../../../static/img/company-logos/fpjs.svg'

import SubscribeNewsletterForm from '../SubscribeNewsletterForm/SubscribeNewsletterForm'
import { Forms } from '../../hooks/useForm'

import Container from '../common/Container'
import styles from './Footer.module.scss'
import { developers, product, resources, useCases } from '../../constants/content'
import { PATH, URL, DOC_URL } from '../../constants/content'
import classNames from 'classnames'
import StatusBadge from '../StatusBadge/StatusBadge'

interface FooterLinkSection {
  title: string
  links: { title: string | React.ReactNode; url: string; isLocal?: boolean }[]
}

const footerLinks: FooterLinkSection[] = [
  {
    title: 'Product',
    links: product,
  },
  {
    title: 'Use Cases',
    links: useCases,
  },
  {
    title: 'Resources',
    links: resources,
  },
  {
    title: 'Developers',
    links: developers,
  },
  {
    title: 'Company',
    links: [
      {
        title: 'About us',
        url: PATH.aboutUs,
        isLocal: true,
      },
      {
        title: (
          <>
            Careers
            <div className={styles.labelWrapper}>
              <span className={styles.label}>We&lsquo;re hiring</span>
            </div>
          </>
        ),
        url: PATH.careers,
        isLocal: true,
      },
      {
        title: 'Press',
        url: PATH.press,
        isLocal: true,
      },
      {
        title: 'Partners',
        url: PATH.partners,
        isLocal: true,
      },
      {
        title: 'System status',
        url: URL.statusUrl,
        isLocal: false,
      },
      {
        title: 'Security',
        url: PATH.security,
        isLocal: true,
      },
    ],
  },
]

interface FooterProps extends React.HTMLAttributes<HTMLDivElement> {
  darkVariant?: boolean
  shortVersion?: boolean
  footerBackground?: boolean
}

export default function Footer({
  darkVariant,
  shortVersion = false,
  footerBackground = false,
  className,
  ...props
}: FooterProps) {
  const currentYear = new Date().getFullYear()

  return (
    <footer
      className={classNames(styles.footer, {
        [styles.dark]: darkVariant,
        [styles.short]: shortVersion,
        [styles.background]: footerBackground,
        className,
      })}
      {...props}
    >
      {!shortVersion && (
        <Container size='large' className={styles.navContainer}>
          <nav className={styles.nav}>
            <section className={styles.logo}>
              <FpjsSVG className={styles.fpjsLogo} />
              <div className={styles.newsLetter}>
                <p className={styles.title}>
                  Subscribe to our <span className={styles.newsletterLabel}>newsletter_</span>
                </p>
                <SubscribeNewsletterForm
                  size='large'
                  variant={darkVariant ? 'dark' : 'white'}
                  origin={Forms.NewsletterFooter}
                />
              </div>
            </section>
            <section className={styles.links}>
              {footerLinks.map(({ title, links }) => {
                return (
                  <div key={title} className={styles.menu}>
                    <h3 className={styles.title}>{title}</h3>
                    {links.map(({ title, url, isLocal }) => {
                      return isLocal ? (
                        <Link className={styles.link} key={url} to={url} rel='noreferrer'>
                          {title}
                        </Link>
                      ) : (
                        <a className={styles.link} key={url} href={url} rel='noreferrer'>
                          {title}
                        </a>
                      )
                    })}
                  </div>
                )
              })}
            </section>
            <section className={styles.newsLetterMobile}>
              <p className={styles.title}>
                Subscribe to our <span className={styles.newsletterLabel}>newsletter_</span>
              </p>
              <SubscribeNewsletterForm
                size='large'
                variant={darkVariant ? 'dark' : 'white'}
                origin={Forms.NewsletterFooter}
              />
            </section>
          </nav>
        </Container>
      )}
      <Container
        size='large'
        className={classNames(styles.contactContainer, {
          [styles.short]: shortVersion,
          [styles.background]: footerBackground,
        })}
      >
        <div className={styles.contactWrapper}>
          <section className={styles.copySection}>
            <span className={styles.copyright}>&copy; {currentYear} FingerprintJS, Inc</span>
            <div className={styles.termsLinks}>
              <a href={DOC_URL.privacyPolicyUrl} target='_blank' rel='noreferrer' aria-label='Privacy policy link'>
                Privacy policy
              </a>
              <a href={DOC_URL.termOfUseUrl} target='_blank' rel='noreferrer' aria-label='Terms and conditions link'>
                Terms and conditions
              </a>
            </div>
          </section>
          <section className={styles.socialSection}>
            <div className={styles.discordInvitation}>
              Join our developer community on Discord
              <InvitationSVG className={styles.invitationIcon} />
            </div>
            <ul className={styles.links}>
              <li className={styles.link}>
                <a href={URL.discordServerURL} target='_blank' rel='noreferrer' aria-label='Discord link'>
                  <DiscordSVG />
                </a>
              </li>
              <li className={styles.link}>
                <a href={URL.linkedinUrl} target='_blank' rel='noreferrer' aria-label='LinkedIn link'>
                  <LinkedInSvg />
                </a>
              </li>
              <li className={styles.link}>
                <a href={URL.twitterUrl} target='_blank' rel='noreferrer' aria-label='Twitter link'>
                  <TwitterSvg />
                </a>
              </li>
              <li className={styles.link}>
                <a href={URL.githubRepoUrl} target='_blank' rel='noreferrer' aria-label='GitHub link'>
                  <GithubSVG />
                </a>
              </li>
            </ul>
            <StatusBadge />
          </section>
        </div>
      </Container>
    </footer>
  )
}
